import Table from "@material-ui/core/Table";
import TableContainer from "@material-ui/core/TableContainer";
import ContentTitle from "components/common/ContentTitle";
import Steper from "components/common/Steper";
import ButtonController from "components/request-document/components/ButtonController";
import { useStyles } from "components/request-document/components/Commons";
import HealthInsurance from "components/request-document/components/HealthInsurance";
import useSmartForm from "components/request-document/hooks/useSmartForm";
import useSmartService from "components/request-document/hooks/useSmartService";
import dayjs from "dayjs";
import { useFormik } from "formik";
import {
  HealthStatus,
  REQUEST_DOCUMENT_STEP,
  RoleId,
  STATUS_PROCESS
} from "libs/configs/constant";
import { SMART_T_STEP } from "libs/constant/Step";
import useModalLoading from "libs/hooks/useModalLoading";
import _ from "lodash";
import { get } from "lodash-es";
import mime from "mime";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { canEditForm } from "redux/modules/authSlice";
import {
  addHealthInsurance,
  getHealthInsuranceList,
  healthInsuranceAddSlice,
  healthInsuranceListSlice,
  selectAddHealthInsurance,
  selectListHealthInsurance
} from "redux/modules/healthInsuranceSlice";
import { selectPersonalDetail } from "redux/modules/personalSlice";
import { setSmartFormStep } from "redux/modules/uiSlice";
import { selectDraftId } from "redux/modules/visaForm";
import * as yup from "yup";

export default function HealthInsuranceStep() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { showLoading, hideLoading } = useModalLoading();
  const [isSaveDraft, setSaveDraft] = useState(true);
  const [isFirstTimeFetching, setFirstTimeFetching] = useState(true);

  const { smartserviceId } = useSmartService();
  const { getSmartForm, smartform } = useSmartForm();

  const canEdit = useSelector(canEditForm);
  const personalDetail = useSelector(selectPersonalDetail);
  const draftId = useSelector(selectDraftId);
  const healthInsuranceListState = useSelector(selectListHealthInsurance);
  const healthInsuranceAddState = useSelector(selectAddHealthInsurance);

  const personalId = get(personalDetail, "personalID", "");
  const healthInsuranceList = get(healthInsuranceListState, "list", []);
  const isRHIAllowUserEdit =
    smartform.healthStatus === HealthStatus.Draft ||
    smartform.healthStatus === HealthStatus.RequestDocument ||
    smartform.healthStatus === null;

  useEffect(() => {
    if (smartserviceId) {
      getSmartForm({
        smartserviceId,
        draftId,
        personalId,
      });
    }
  }, [dispatch, draftId, getSmartForm, personalId, smartserviceId]);

  useEffect(() => {
    if (draftId && personalId && isFirstTimeFetching) {
      setFirstTimeFetching(false);
      dispatch(getHealthInsuranceList({ personalID: personalId }));
      showLoading();
    }
  }, [personalId, draftId, dispatch, showLoading, isFirstTimeFetching]);

  useEffect(() => {
    if (!healthInsuranceListState.isLoading) {
      if (healthInsuranceListState.isSuccess) {
        hideLoading();
        dispatch(healthInsuranceListSlice.actions.clearAsyncState());
      } else if (healthInsuranceListState.isFailure) {
        hideLoading();
        alert(healthInsuranceListState.error?.message);
        dispatch(healthInsuranceListSlice.actions.clearAsyncState());
      }
    }
  }, [
    healthInsuranceListState.isLoading,
    healthInsuranceListState.isSuccess,
    healthInsuranceListState.isFailure,
    healthInsuranceListState.error?.message,
    hideLoading,
    dispatch,
  ]);

  useEffect(() => {
    if (!healthInsuranceAddState.isLoading) {
      if (healthInsuranceAddState.isSuccess) {
        hideLoading();
        dispatch(healthInsuranceAddSlice.actions.clearAsyncState());
        dispatch(getHealthInsuranceList({ personalID: personalId }));
        if (!isSaveDraft) {
          dispatch(setSmartFormStep(SMART_T_STEP.VISA_INSUANCE));
        }
      } else if (healthInsuranceAddState.isFailure) {
        hideLoading();
        alert(healthInsuranceAddState.error?.message);
        dispatch(healthInsuranceAddSlice.actions.clearAsyncState());
      }
    }
  }, [
    dispatch,
    draftId,
    healthInsuranceAddState.error?.message,
    healthInsuranceAddState.isFailure,
    healthInsuranceAddState.isLoading,
    healthInsuranceAddState.isSuccess,
    hideLoading,
    isSaveDraft,
    personalId,
  ]);

  const changeStatusIfValid = (value = {}, isDraft) => {
    const isValid = value.FileID && value.startDate && value.expDate;
    const newStatus = isValid ? HealthStatus.Pending : HealthStatus.Draft;

    if (isDraft) {
      return {
        ...value,
        Status:
          value.Status === HealthStatus.Draft
            ? HealthStatus.Draft
            : value.Status,
      };
    }

    return {
      ...value,
      Status: newStatus,
    };
  };

  const callAPISubmit = async (values, isDraft = true) => {
    showLoading();

    const HealthInsuranceList = values.HealthInsuranceList;
    const HealthInsuranceListWithoutApprove = HealthInsuranceList.filter(
      (v) => v.Status !== HealthStatus.Approved
    );
    // const prepareData = HealthInsuranceList[HealthInsuranceList.length - 1];
    const prepareData = HealthInsuranceListWithoutApprove.map((value) =>
      changeStatusIfValid(
        _.omit(value, ["file", "disabled", "remark"]),
        isDraft
      )
    );

    await dispatch(
      addHealthInsurance({
        draftId,
        healthInsurance: prepareData,
        isRHI: smartform?.statausID === STATUS_PROCESS.ReviewingHealthInsurance,
      })
    );
  };

  const mapHealthInsurance = (field) => {
    const Status = field.status || HealthStatus.Draft;
    return {
      healthID: field.healthID || null,
      personalID: field.personalID || null,
      draftId: field.draftId || null,
      startDate: field.startDate
        ? dayjs(field.startDate).format("YYYY-MM-DD")
        : null,
      expDate: field.expDate ? dayjs(field.expDate).format("YYYY-MM-DD") : null,
      remark: field.remark || null,
      FileID: field.fileID || null,
      Status,
      file: field.fileID
        ? new File([new Blob()], `${field.filename}${field.ext}`, {
            type: mime.getType(field.ext),
          })
        : {},
      // disabled: field.draftId ? field.draftId !== draftId : false,
      disabled: !isRHIAllowUserEdit || Status === HealthStatus.Approved,
    };
  };

  const validationSchema = yup.object({
    HealthInsuranceList: yup.array().of(
      yup.object().shape({
        FileID: yup.string().nullable().required("Document file is required."),
        startDate: yup.string().nullable().required("Start date is required."),
        expDate: yup.string().nullable().required("Expiry date is required."),
      })
    ),
  });

  const defaultHealthInsuranceItem = {
    healthID: null,
    personalID: personalId,
    draftId: draftId,
    startDate: null,
    expDate: null,
    FileID: null,
    Status: HealthStatus.Draft,
    // --
    file: {},
  };

  const formik = useFormik({
    enableReinitialize: true,
    // validationSchema,
    initialValues: {
      HealthInsuranceList:
        healthInsuranceList.length === 0
          ? [defaultHealthInsuranceItem]
          : healthInsuranceList.map(mapHealthInsurance),
    },
    onSubmit: async (values) => {
      if (!isRHIAllowUserEdit || !canEdit) {
        dispatch(setSmartFormStep(SMART_T_STEP.VISA_INSUANCE));
        return;
      }
      setSaveDraft(false);
      callAPISubmit(values, false);
    },
  });

  const isDraftDocument = smartform?.statausID === STATUS_PROCESS.Draft;
  const Role = useSelector((e) => {
    return e.user.profile.roleId;
  });
  // Disabled all field if status != draft
  if (!isDraftDocument && !isRHIAllowUserEdit && Role !== RoleId.Admin) {
    formik.handleBlur = () => {};
    formik.handleChange = () => {};
    formik.setFieldValue = () => {};
  }

  const checkShouldDisabledSaveDraft = () => {
    const HealthInsuranceList = formik.values.HealthInsuranceList;
    const lastValue = HealthInsuranceList[HealthInsuranceList.length - 1];

    const hasFile = lastValue.FileID;
    const hasSomeDateValue = lastValue?.startDate || lastValue?.expDate;
    const hasAllDateValue = lastValue?.startDate && lastValue?.expDate;

    if (hasSomeDateValue) {
      return !hasAllDateValue;
    } else if (!hasFile && !hasSomeDateValue) {
      return true;
    }

    return false;
  };

  return (
    <div className={classes.root}>
      <form onSubmit={formik.handleSubmit}>
        <ContentTitle
          title="Application Form"
          subTitle="APPLICATION FOR QUALIFICATION ENDORSEMENT FOR SMART VISA “S”"
        />
        <Steper currentStep={1} stepInfo={REQUEST_DOCUMENT_STEP} />
        <TableContainer style={{ marginTop: 35, borderRadius: 4 }}>
          <Table className={classes.table}>
            <HealthInsurance
              colSpan={4}
              formik={formik}
              isShowAddRow={isRHIAllowUserEdit}
              onAddRow={() => {
                formik.setFieldValue("HealthInsuranceList", [
                  ...formik.values.HealthInsuranceList,
                  defaultHealthInsuranceItem,
                ]);
              }}
            />
          </Table>
        </TableContainer>
        <ButtonController
          // isDisabledSaveDraftButton={!isRHIAllowUserEdit}
          // isDisabledSaveDraftButton={checkShouldDisabledSaveDraft()}
          onClickSaveDraft={async () => {
            if (!isRHIAllowUserEdit || !canEdit) {
              dispatch(setSmartFormStep(SMART_T_STEP.VISA_INSUANCE));
              return;
            }
            setSaveDraft(true);
            callAPISubmit(formik.values);
          }}
        />
      </form>
    </div>
  );
}
